// components/CookieBanner.tsx
"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Cookie, X, Shield } from "lucide-react"
import { Button } from "@/components/ui/button"
import Link from "next/link"
import { getLangSegment, segmentToLang } from "@/lib/lang-preference"

type Language = 'es' | 'gl' | 'en'

// Cookie helpers — expiration set to 12 months (RGPD recommendation)
const COOKIE_NAME = "cookieConsent"
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 365 days in seconds

function setCookieConsent(value: "all" | "necessary") {
  document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax; Secure`
}

function getCookieConsent(): string | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`))
  return match ? match[1] : null
}

const bannerTranslations = {
  es: {
    title: "Uso de cookies",
    description: "Utilizamos cookies propias y de terceros para analizar el tráfico, mejorar nuestros servicios y garantizar la seguridad del sitio. Al hacer clic en \"Aceptar\", consientes el uso de todas las cookies.",
    accept: "Aceptar",
    reject: "Solo necesarias",
    moreInfo: "Política de cookies",
  },
  gl: {
    title: "Uso de cookies",
    description: "Utilizamos cookies propias e de terceiros para analizar o tráfico, mellorar os nosos servizos e garantir a seguridade do sitio. Ao facer clic en \"Aceptar\", consintes o uso de todas as cookies.",
    accept: "Aceptar",
    reject: "Só necesarias",
    moreInfo: "Política de cookies",
  },
  en: {
    title: "Cookie usage",
    description: "We use our own and third-party cookies to analyze traffic, improve our services and ensure site security. By clicking \"Accept\", you consent to the use of all cookies.",
    accept: "Accept",
    reject: "Necessary only",
    moreInfo: "Cookie Policy",
  },
} as const

function detectLanguage(): Language {
  if (typeof window === "undefined") return "es"

  // Check URL path first
  const path = window.location.pathname
  if (path.startsWith("/en")) return "en"
  if (path.startsWith("/gal")) return "gl"
  if (path.startsWith("/es")) return "es"

  // Idioma guardado (cookie `lang`, `es` si no hay)
  return segmentToLang(getLangSegment())
}

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const [lang, setLang] = useState<Language>("es")

  useEffect(() => {
    const consent = getCookieConsent()
    if (!consent) {
      // Small delay for smoother UX — let the page render first
      const timer = setTimeout(() => setIsVisible(true), 1000)
      return () => clearTimeout(timer)
    }
    setLang(detectLanguage())
  }, [])

  useEffect(() => {
    setLang(detectLanguage())
  }, [isVisible])

  const handleAccept = () => {
    setCookieConsent("all")
    setIsVisible(false)
  }

  const handleRejectOptional = () => {
    setCookieConsent("necessary")
    setIsVisible(false)
  }

  const t = bannerTranslations[lang]

  // Determine cookie policy link based on current lang path
  const getCookiePolicyLink = () => {
    const path = typeof window !== "undefined" ? window.location.pathname : ""
    if (path.startsWith("/en")) return "/en/legal/politica-cookies"
    if (path.startsWith("/gal")) return "/gal/legal/politica-cookies"
    if (path.startsWith("/es")) return "/es/legal/politica-cookies"
    return "/legal/politica-cookies"
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[9999]"
        >
          <div className="relative border-t border-border/50 bg-white/95 backdrop-blur-xl shadow-[0_-4px_30px_rgba(0,0,0,0.08)]">
            {/* Accent bar at top */}
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(to right, #0e918c, #14b8a6, #0e918c)' }} />

            <div className="px-6 md:px-16 lg:px-32 xl:px-40 py-4 md:py-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
                {/* Icon + text */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: 'rgba(14, 145, 140, 0.1)' }}>
                      <Shield className="h-4.5 w-4.5" style={{ color: '#0e918c' }} />
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <span className="font-semibold text-gray-900">{t.title}:</span>{" "}
                    {t.description}{" "}
                    <Link
                      href={getCookiePolicyLink()}
                      className="inline-flex items-center gap-1 font-medium underline underline-offset-2 transition-colors"
                      style={{ color: '#0e918c' }}
                    >
                      {t.moreInfo}
                    </Link>
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex items-center gap-3 flex-shrink-0 ml-12 md:ml-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRejectOptional}
                    className="rounded-lg border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900 text-sm px-4 h-9 cursor-pointer"
                  >
                    {t.reject}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAccept}
                    className="rounded-lg text-white text-sm px-5 h-9 cursor-pointer"
                    style={{ backgroundColor: '#0e918c', boxShadow: '0 4px 6px -1px rgba(14, 145, 140, 0.2)' }}
                  >
                    {t.accept}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
